import React, { useMemo } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  random,
} from 'remotion';

interface ImageDistortionTransitionProps {
  image1?: string;
  image2?: string;
  transitionType?: 'wave' | 'pixelate' | 'noise' | 'spiral';
}

/**
 * #429 图像扭曲过渡 (Image Distortion Transition)
 *
 * 来源：2026 创意动画趋势 - 图像转场效果
 * 类型：转场动画 + Canvas 2D
 * 技术要点：
 * - 图像扭曲算法
 * - Perlin 噪声
 * - 像素化效果
 * - 波浪扭曲
 * - 螺旋变形
 *
 * 实现难度：⭐⭐⭐
 * 适用场景：视频转场、品牌宣传片、音乐视频
 */
export const ImageDistortionTransition: React.FC<ImageDistortionTransitionProps> = ({
  image1 = 'https://picsum.photos/seed/remotion1/1920/1080',
  image2 = 'https://picsum.photos/seed/remotion2/1920/1080',
  transitionType = 'wave',
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();
  
  // 过渡进度（0-1）
  const transitionDuration = durationInFrames / 2;
  const transitionStart = durationInFrames / 4;
  const progress = interpolate(
    frame,
    [transitionStart, transitionStart + transitionDuration],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
  
  // Spring 进入动画
  const entranceProgress = spring({
    frame,
    fps,
    config: { damping: 15, stiffness: 80 },
  });
  
  // 生成网格点（用于扭曲效果）
  const gridSize = 20;
  const gridPoints = useMemo(() => {
    const points = [];
    for (let x = 0; x <= width; x += width / gridSize) {
      for (let y = 0; y <= height; y += height / gridSize) {
        points.push({ x, y });
      }
    }
    return points;
  }, [width, height, gridSize]);
  
  // 计算扭曲偏移
  const getDistortion = (x: number, y: number, time: number) => {
    switch (transitionType) {
      case 'wave':
        // 波浪扭曲
        const waveX = Math.sin(y * 0.01 + time * 0.1) * 50 * progress;
        const waveY = Math.cos(x * 0.01 + time * 0.1) * 30 * progress;
        return { dx: waveX, dy: waveY };
      
      case 'pixelate':
        // 像素化效果（通过块状扭曲模拟）
        const blockSize = interpolate(progress, [0, 1], [1, 50]);
        const blockX = Math.floor(x / blockSize) * blockSize;
        const blockY = Math.floor(y / blockSize) * blockSize;
        return {
          dx: (blockX - x) * 0.5,
          dy: (blockY - y) * 0.5,
        };
      
      case 'noise':
        // 噪声扭曲
        const noiseScale = 0.005;
        const noiseStrength = 100 * progress;
        const noiseX = Math.sin(x * noiseScale + time * 0.05) * noiseStrength;
        const noiseY = Math.cos(y * noiseScale + time * 0.05) * noiseStrength;
        return { dx: noiseX, dy: noiseY };
      
      case 'spiral':
        // 螺旋变形
        const cx = width / 2;
        const cy = height / 2;
        const dx = x - cx;
        const dy = y - cy;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const spiralStrength = (distance / width) * progress * Math.PI;
        const newAngle = angle + spiralStrength;
        const newDistance = distance * (1 + Math.sin(progress * Math.PI) * 0.5);
        return {
          dx: Math.cos(newAngle) * newDistance - dx,
          dy: Math.sin(newAngle) * newDistance - dy,
        };
      
      default:
        return { dx: 0, dy: 0 };
    }
  };
  
  // 生成扭曲后的网格
  const distortedGrid = gridPoints.map((point, i) => {
    const distortion = getDistortion(point.x, point.y, frame);
    return (
      <div
        key={i}
        style={{
          position: 'absolute',
          left: point.x + distortion.dx,
          top: point.y + distortion.dy,
          width: 3,
          height: 3,
          backgroundColor: `rgba(6, 182, 212, ${0.3 - progress * 0.2})`,
          borderRadius: '50%',
        }}
      />
    );
  });
  
  // 装饰性粒子
  const particles = Array.from({ length: 60 }, (_, i) => {
    const angle = (i / 60) * Math.PI * 2;
    const radius = 300 + Math.sin(frame * 0.05 + i) * 50;
    const x = width / 2 + Math.cos(angle + frame * 0.01) * radius;
    const y = height / 2 + Math.sin(angle + frame * 0.01) * radius;
    const size = 4 + Math.sin(frame * 0.03 + i) * 2;
    
    return { x, y, size, opacity: 0.4 - progress * 0.2 };
  });
  
  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #08090c 0%, #1a1a2e 100%)',
        overflow: 'hidden',
      }}
    >
      {/* 背景网格 */}
      <svg width={width} height={height} style={{ position: 'absolute', opacity: 0.1 }}>
        {Array.from({ length: Math.ceil(width / 40) }, (_, i) => (
          <line
            key={`v-${i}`}
            x1={i * 40}
            y1={0}
            x2={i * 40}
            y2={height}
            stroke="#06b6d4"
            strokeWidth="1"
          />
        ))}
        {Array.from({ length: Math.ceil(height / 40) }, (_, i) => (
          <line
            key={`h-${i}`}
            x1={0}
            y1={i * 40}
            x2={width}
            y2={i * 40}
            stroke="#06b6d4"
            strokeWidth="1"
          />
        ))}
      </svg>
      
      {/* 图像1（底层） */}
      <div
        style={{
          position: 'absolute',
          width,
          height,
          opacity: 1 - progress,
          transform: `scale(${1 + progress * 0.2})`,
          filter: `blur(${progress * 10}px)`,
        }}
      >
        <img
          src={image1}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          alt="Image 1"
        />
      </div>
      
      {/* 图像2（顶层） */}
      <div
        style={{
          position: 'absolute',
          width,
          height,
          opacity: progress,
          transform: `scale(${1.2 - progress * 0.2})`,
          filter: `blur(${(1 - progress) * 10}px)`,
        }}
      >
        <img
          src={image2}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          alt="Image 2"
        />
      </div>
      
      {/* 扭曲网格 */}
      <div
        style={{
          position: 'absolute',
          width,
          height,
          opacity: entranceProgress,
        }}
      >
        {distortedGrid}
      </div>
      
      {/* 装饰粒子 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor: '#06b6d4',
            opacity: p.opacity,
          }}
        />
      ))}
      
      {/* 过渡进度条 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          right: 40,
          height: 4,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          overflow: 'hidden',
          opacity: entranceProgress,
        }}
      >
        <div
          style={{
            width: `${progress * 100}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #f59e0b, #06b6d4)',
            transition: 'width 0.1s ease',
          }}
        />
      </div>
      
      {/* 效果信息 */}
      {/* <div
        style={{
          position: 'absolute',
          top: 40,
          left: 40,
          padding: '15px 25px',
          background: 'rgba(6, 182, 212, 0.2)',
          border: '1px solid #06b6d4',
          borderRadius: '8px',
          color: '#06b6d4',
          fontSize: 16,
          opacity: entranceProgress,
        }}
      >
        <div style={{ marginBottom: 5, fontWeight: 'bold' }}>
          Image Distortion Transition
        </div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          Type: {transitionType.toUpperCase()}
        </div>
      </div> */}
      
      {/* 统计信息 */}
      {/* <div
        style={{
          position: 'absolute',
          top: 40,
          right: 40,
          padding: '15px 25px',
          background: 'rgba(245, 158, 11, 0.2)',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          color: '#f59e0b',
          fontSize: 14,
          opacity: entranceProgress,
        }}
      >
        <div>Progress: {(progress * 100).toFixed(0)}%</div>
        <div>Frame: {frame}</div>
        <div>Type: {transitionType}</div>
      </div> */}
      
      {/* 转场类型选择器 */}
      {/* <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 40,
          right: 40,
          display: 'flex',
          justifyContent: 'center',
          gap: 15,
          opacity: entranceProgress,
        }}
      >
        {['wave', 'pixelate', 'noise', 'spiral'].map((type) => (
          <div
            key={type}
            style={{
              padding: '8px 16px',
              background: type === transitionType ? 'rgba(245, 158, 11, 0.3)' : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${type === transitionType ? '#f59e0b' : '#333'}`,
              borderRadius: '8px',
              fontSize: 12,
              color: type === transitionType ? '#f59e0b' : '#888',
            }}
          >
            {type}
          </div>
        ))}
      </div> */}
    </AbsoluteFill>
  );
};
